// ─────────────────────────────────────────────────────────────
// calendarUtils.js
// ICS file generation and Google Calendar URL construction.
//
// Two public generators:
//   generateSingleEventICS(event, location, siteUrl, calendarDescription)
//   generateSeriesICS(recurringSettings, defaultLocation, siteUrl, calendarDescription)
//
// One Google Calendar URL builder (single event only — Google's
// URL scheme does not support recurring events):
//   googleCalendarUrl(event, location, siteUrl)
//
// One download helper:
//   downloadICS(icsString, filename)
// ─────────────────────────────────────────────────────────────

import SITE from '../site';

// ── Helpers ───────────────────────────────────────────────────

// Format a JS Date as an ICS UTC datetime string: 20260412T160000Z
const toICSDateTime = (date) => {
  const pad = (n) => String(n).padStart(2, '0');
  return (
    date.getUTCFullYear() +
    pad(date.getUTCMonth() + 1) +
    pad(date.getUTCDate()) +
    'T' +
    pad(date.getUTCHours()) +
    pad(date.getUTCMinutes()) +
    pad(date.getUTCSeconds()) +
    'Z'
  );
};

// Format a JS Date as an ICS local date string (no time): 20260412
const toICSDate = (date) => {
  const pad = (n) => String(n).padStart(2, '0');
  return (
    date.getUTCFullYear() +
    pad(date.getUTCMonth() + 1) +
    pad(date.getUTCDate())
  );
};

// Escape text for ICS property values (RFC 5545)
const escapeICS = (text) => {
  if (!text) return '';
  return text
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\n/g, '\\n');
};

// Fold long lines at 75 chars as required by RFC 5545
const foldLine = (line) => {
  if (line.length <= 75) return line;
  const parts = [];
  let remaining = line;
  parts.push(remaining.slice(0, 75));
  remaining = remaining.slice(75);
  while (remaining.length > 0) {
    parts.push(' ' + remaining.slice(0, 74));
    remaining = remaining.slice(74);
  }
  return parts.join('\r\n');
};

// Generate a unique UID for an ICS event
const makeUID = (seed) => {
  return `${seed}-${Date.now()}@${SITE.slug}`;
};

// Day-of-week number (0=Sun) to ICS BYDAY code
const DAY_TO_BYDAY = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'];

// Wrap lines and join as ICS content block
const buildICS = (lines) => {
  return lines.map(foldLine).join('\r\n') + '\r\n';
};

// ── Single Event ICS ──────────────────────────────────────────

export const generateSingleEventICS = (event, location, siteUrl, calendarDescription) => {
  const eventDate = event.date.toDate();
  // Default duration: 2 hours
  const endDate = new Date(eventDate.getTime() + 2 * 60 * 60 * 1000);

  const title = event.title || 'Weekly Pickup Ultimate';
  const locationStr = location
    ? [location.name, location.address].filter(Boolean).join(', ')
    : '';

  const descParts = [];
  if (event.description) {
    descParts.push(event.description.replace(/<[^>]+>/g, ''));
  }
  if (calendarDescription) descParts.push(calendarDescription);
  if (siteUrl) descParts.push(`RSVP: ${siteUrl}`);
  const description = descParts.join('\n\n');

  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    `PRODID:-//${SITE.name}//RSVP//EN`,
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${makeUID(event.id)}`,
    `DTSTAMP:${toICSDateTime(new Date())}`,
    `DTSTART:${toICSDateTime(eventDate)}`,
    `DTEND:${toICSDateTime(endDate)}`,
    `SUMMARY:${escapeICS(title)}`,
    locationStr ? `LOCATION:${escapeICS(locationStr)}` : null,
    description ? `DESCRIPTION:${escapeICS(description)}` : null,
    siteUrl ? `URL:${siteUrl}` : null,
    'END:VEVENT',
    'END:VCALENDAR',
  ].filter(Boolean);

  return buildICS(lines);
};

// ── Series ICS ────────────────────────────────────────────────

export const generateSeriesICS = (
  recurringSettings,
  defaultLocation,
  siteUrl,
  calendarDescription,
  weeksAhead = 52  // 12 months
) => {
  if (!recurringSettings || !recurringSettings.enabled) return null;

  const { day, time, title, description } = recurringSettings;
  const [hours, minutes] = (time || '09:00').split(':').map(Number);
  const dayNum = parseInt(day || '6');
  const byDay = DAY_TO_BYDAY[dayNum];

  // Find the first occurrence on or after today
  const now = new Date();
  const startDate = new Date(now);
  // Advance to the correct day of week
  const todayDow = startDate.getDay();
  let daysUntilFirst = (dayNum - todayDow + 7) % 7;
  // If it's today but the time has passed, go to next week
  if (daysUntilFirst === 0) {
    const todayAtTime = new Date(startDate);
    todayAtTime.setHours(hours, minutes, 0, 0);
    if (now > todayAtTime) daysUntilFirst = 7;
  }
  startDate.setDate(startDate.getDate() + daysUntilFirst);
  startDate.setHours(hours, minutes, 0, 0);

  // UNTIL = 12 months from today
  const until = new Date(now);
  until.setFullYear(until.getFullYear() + 1);

  // Duration: 2 hours
  const endDate = new Date(startDate.getTime() + 2 * 60 * 60 * 1000);

  const locationStr = defaultLocation
    ? [defaultLocation.name, defaultLocation.address].filter(Boolean).join(', ')
    : '';

  const descParts = [];
  if (description) descParts.push(description);
  if (calendarDescription) descParts.push(calendarDescription);
  if (siteUrl) descParts.push(`Check RSVP status before coming: ${siteUrl}`);
  const fullDescription = descParts.join('\n\n');

  const rrule = `RRULE:FREQ=WEEKLY;BYDAY=${byDay};UNTIL=${toICSDate(until)}`;

  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    `PRODID:-//${SITE.name}//RSVP//EN`,
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    `X-WR-CALNAME:${escapeICS(title || 'Weekly Pickup Ultimate')}`,
    'BEGIN:VEVENT',
    `UID:${makeUID('series')}`,
    `DTSTAMP:${toICSDateTime(new Date())}`,
    `DTSTART:${toICSDateTime(startDate)}`,
    `DTEND:${toICSDateTime(endDate)}`,
    rrule,
    `SUMMARY:${escapeICS(title || 'Weekly Pickup Ultimate')}`,
    locationStr ? `LOCATION:${escapeICS(locationStr)}` : null,
    fullDescription ? `DESCRIPTION:${escapeICS(fullDescription)}` : null,
    siteUrl ? `URL:${siteUrl}` : null,
    'END:VEVENT',
    'END:VCALENDAR',
  ].filter(Boolean);

  return buildICS(lines);
};

// ── Google Calendar URL (single event only) ───────────────────

export const googleCalendarUrl = (event, location, siteUrl) => {
  const eventDate = event.date.toDate();
  const endDate = new Date(eventDate.getTime() + 2 * 60 * 60 * 1000);

  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: event.title || 'Weekly Pickup Ultimate',
    dates: `${toICSDateTime(eventDate)}/${toICSDateTime(endDate)}`,
    details: siteUrl ? `RSVP at ${siteUrl}` : 'Check the site to RSVP',
    location: location ? [location.name, location.address].filter(Boolean).join(', ') : '',
  });

  return `https://calendar.google.com/calendar/render?${params.toString()}`;
};

// ── Download helper ───────────────────────────────────────────

export const downloadICS = (icsString, filename = 'event.ics') => {
  const blob = new Blob([icsString], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};
